/* Документ итога «Текста» без React (план вехи 3, text-cover §2.2 «text-result.ts»): те же правила, что у родного хука
   (native/use-text-result.ts) — текст прямо в result.data / result.text, иначе защищённая выгрузка файла итога JSON или TXT не больше 512 КБ
   за 30 с; владелец проверяется до и после выгрузки; неизвестный JSON — не готовый текст. Один документ на владельца, заказ и ревизию
   итога (owner:id:revision): смена ревизии читает заново, «Повторить» — новая попытка того же заказа. */
import { endpoints } from '../../api';
import type { SpeechOrder } from '../../api/speech';
import { parseTextResult, type TextDocument } from './native/text-result-model';

const MIMES = ['application/json', 'text/plain', 'text/json'];
const LIMIT = 512 * 1024;
const DEADLINE_MS = 30_000;

export interface TextResultState {
  /** Документ итога (null — его нет или он ещё читается) */
  doc: TextDocument | null;
  /** Документ не открылся (нет файла текста, отказ выгрузки, срок, чужой владелец) */
  error: boolean;
}
export interface TextResults {
  /** Документ готового заказа; если его надо выгрузить — выгрузка начинается (один раз на owner:id:revision) */
  get(order: SpeechOrder | null | undefined): TextResultState;
  /** «Повторить»: прежняя неудача забывается, выгрузка заново */
  retry(order: SpeechOrder | null | undefined): void;
  /** Кадр ушёл: выгрузки прерываются, их ответы никуда не ложатся */
  stop(): void;
}

interface Held {
  doc: TextDocument | null;
  error: boolean;
  flight: AbortController | null;
}

export function createTextResults(o: { owner(): number | undefined; changed(): void }): TextResults {
  const held = new Map<string, Held>();
  const identity = (order: SpeechOrder) => `${o.owner()}:${order.id}:${order.result_revision}`;
  const assetOf = (order: SpeechOrder) => order.result?.assets?.find(item => MIMES.includes(item.mime));
  function load(order: SpeechOrder, id: string, at: Held) {
    const asset = assetOf(order);
    const owner = o.owner();
    if (!asset || !owner) {
      at.error = true;
      return;
    }
    const controller = new AbortController();
    at.flight = controller;
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, DEADLINE_MS);
    void (async () => {
      try {
        if ((await endpoints.me()).user.id !== owner || controller.signal.aborted) throw new Error('owner_changed');
        const blob = await endpoints.speech.result(order, asset.index, controller.signal);
        if (blob.size > LIMIT) throw new Error('too_large');
        const text = await blob.text();
        const data: unknown = asset.mime === 'text/plain' ? { text } : JSON.parse(text);
        const doc = parseTextResult(data);
        if (!doc) throw new Error('unsupported');
        if ((await endpoints.me()).user.id !== owner || controller.signal.aborted) throw new Error('owner_changed');
        at.doc = doc;
        at.error = false;
      } catch {
        if (!controller.signal.aborted || timedOut) at.error = true;
      } finally {
        clearTimeout(timer);
        if (at.flight === controller) at.flight = null;
        if (held.get(id) === at && (!controller.signal.aborted || timedOut)) o.changed();
      }
    })();
  }
  return {
    get(order) {
      if (!order || order.status !== 'done' || !order.result?.available) return { doc: null, error: false };
      const direct = parseTextResult(order.result.data ?? (order.result as { text?: unknown }).text);
      if (direct) return { doc: direct, error: false };
      const id = identity(order);
      let at = held.get(id);
      if (!at) {
        at = { doc: null, error: false, flight: null };
        held.set(id, at);
        load(order, id, at);
      }
      return { doc: at.doc, error: at.error };
    },
    retry(order) {
      if (!order) return;
      const id = identity(order);
      held.get(id)?.flight?.abort();
      held.delete(id);
      o.changed();
    },
    stop() {
      for (const at of held.values()) at.flight?.abort();
      held.clear();
    },
  };
}
